import mixpanel from 'mixpanel-browser';

/**
 * Mixpanel Analytics plugin
 * @link https://github.com/davidwells/analytics?tab=readme-ov-file#creating-analytics-plugins
 * @param {object} pluginConfig - Plugin settings
 * @param {string} pluginConfig.token - The mixpanel token associated to a mixpanel project
 * @param {object} [pluginConfig.options] - The mixpanel init options https://github.com/mixpanel/mixpanel-js/blob/7d155e6fce7e59f3e13eb2e4d69b9905f10dbb36/src/mixpanel-core.js#L93
 * @param {string} [pluginConfig.pageEvent] - Event name to use for page() events (default to page path)
 * @return {object} Analytics plugin
 * @example
 *
 * mixpanelPlugin({
 *   token: 'abcdef123'
 * })
 */
export function mixpanelPlugin(pluginConfig: any) {
  return {
    name: 'mixpanel',
    config: pluginConfig,
    /* https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript */
    initialize: ({ config }: any) => {
      const { token, options = {} } = config;
      if (!token) {
        throw new Error('No mixpanel token defined');
      }

      mixpanel.init(token, { ...options });
    },
    /**
     * Identify a visitor in mixpanel
     * @link https://docs.mixpanel.com/docs/tracking-methods/id-management/identifying-users
     *
     * Mixpanel doesn't allow to set properties directly in identify, so mixpanel.people.set is
     * also called if properties are passed
     */
    identify: ({ payload }: any) => {
      const { userId, traits } = payload;
      if (typeof userId === 'string') {
        mixpanel.identify(userId);
      }
      if (traits) {
        mixpanel.people.set(traits);
      }
    },
    /**
     * Mixpanel doesn't have a "page" function, so we are using the track method by sending
     * the path as tracked event and search parameters as properties
     */
    page: ({ payload }: any) => {
      mixpanel.track(pluginConfig.pageEvent || payload.properties.path, payload.properties);
    },
    /* https://developer.mixpanel.com/docs/javascript-full-api-reference#mixpaneltrack */
    track: ({ payload }: any) => {
      mixpanel.track(payload.event, payload.properties);
    },
    loaded: () => {
      return mixpanel.hasOwnProperty('__loaded');
    },
    /* Clears super properties and generates a new random distinct_id for this instance. Useful for clearing data when a user logs out. */
    reset: () => {
      mixpanel.reset();
    },
    /* Custom methods to add .alias call */
    methods: {
      /**
       * The alias method creates an alias which Mixpanel will use to remap one id to another. Multiple aliases can point to the same identifier.
       * @link https://developer.mixpanel.com/reference/identity-create-alias
       *
       * @param  {string} [alias] - A unique identifier that you want to use for this user in the future.
       * @param  {string} [original] - The current identifier being used for this user.
       */
      alias(alias: string, original: string) {
        mixpanel.alias(alias, original);
      },
    },
  };
}
